import { useLingui } from "@lingui/react/macro";
import type { Bot } from "@rakazo/contracts";
import {
  chatModelChoices,
  connectedModelChoices,
  modelOptionKey,
  parseModelOptionKey,
  resolveSelectableModelId,
} from "@rakazo/core";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useSpaceQueries } from "../../lib/query";
import { ChatModelPicker } from "./chat-model-picker";

/** The chat bot's model, from the models its connections offer, as in the bot's settings. */
export function ComposerModelPicker({
  bot,
  onChange,
}: {
  bot: Pick<Bot, "id" | "modelProvider" | "modelId">;
  onChange: (model: { modelProvider: string | null; modelId: string | null }) => Promise<void>;
}) {
  const { t } = useLingui();
  const queries = useSpaceQueries();
  const credentials = useQuery(queries.models.credentials.queryOptions()).data;
  const catalog = useQuery(queries.models.list.queryOptions()).data;
  const me = useQuery(queries.me.queryOptions()).data;
  const [saving, setSaving] = useState(false);
  if (!credentials || !catalog) return null;
  const connected = connectedModelChoices(credentials, catalog);
  // Shown only once there is something to choose between.
  if (connected.length < 2) return null;
  const selectedKey =
    bot.modelProvider && bot.modelId
      ? modelOptionKey(
          bot.modelProvider,
          resolveSelectableModelId(catalog, bot.modelProvider, bot.modelId),
        )
      : "";
  const defaultEntry = me?.defaultModel
    ? catalog.find((entry) => entry.provider === me.defaultProvider && entry.id === me.defaultModel)
    : undefined;
  const choices = chatModelChoices(connected, selectedKey, t`Space default`, defaultEntry?.images);
  return (
    <ChatModelPicker
      id={`composer-model-${bot.id}`}
      compact
      disabled={saving}
      choices={choices}
      value={selectedKey}
      onChange={(key) => {
        if (key === selectedKey) return;
        const next = key ? parseModelOptionKey(key) : null;
        setSaving(true);
        void onChange({
          modelProvider: next?.provider ?? null,
          modelId: next?.modelId ?? null,
        }).finally(() => setSaving(false));
      }}
    />
  );
}
